/**
 * Sport Logo Mapping
 *
 * Maps sport codes to their logo asset paths.
 * Used by admin list pages, frontend sport filters, etc.
 */

/** Map from uppercase sport code to logo path */
export const SPORT_LOGO_MAP: Record<string, string> = {
  // Sport codes — generic artwork, no trademark. Safe on any client.
  FOOTBALL: '/american-football-logo.svg',
  BASKETBALL: '/basketball-logo.svg',
  BASEBALL: '/baseball-logo.svg',
  HOCKEY: '/ice-hockey-logo.svg',
  CRICKET: '/cricket-logo.svg',
  // League codes — the league's own mark. Only for a client holding the rights.
  // '/league-icon.svg' IS the NFL shield despite the generic filename. Correct for a client
  // that runs the NFL; never reached as a fallback any more (see DEFAULT_LOGO below).
  NFL: '/league-icon.svg',
  NBA: '/nba-logo.svg',
  NHL: '/nhl-logo.png',
  NCAAMB: '/basketball-logo.svg',
  NCAAFB: '/american-football-logo.svg',
  MLB: '/mlb-logo.svg',
  PGA: '/pga-logo.svg',
  SOCCER: '/soccer-logo.svg',
  // A multi-sport pick'em gets the neutral all-sports mark. It was the
  // incumbent's logo, which rendered on every operator's instance (CAE-659).
  MIXED: '/all-sports-icon.svg',
};

/**
 * The fallback badge for an unknown or absent sport.
 *
 * Was '/league-icon.svg' — which despite its generic name is the NFL shield (its two fills
 * are #D50A0A and #013369, the NFL's brand colours). That made another league's registered
 * trademark the default mark on every client that did not match a mapped sport, including
 * clients that run no sports at all. The neutral all-sports mark carries no league identity,
 * which is what a fallback should do.
 */
const DEFAULT_LOGO = '/all-sports-icon.svg';

/**
 * Get the logo URL for a sport.
 * @param sport - Uppercase sport code (e.g. 'NFL', 'NBA'). Case-insensitive.
 * @returns Logo path string
 */
export function getSportLogoUrl(sport?: string): string {
  if (!sport) return DEFAULT_LOGO;
  return SPORT_LOGO_MAP[sport.toUpperCase()] || DEFAULT_LOGO;
}
